import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import WeatherWidget from '../components/WeatherWidget';
import { 
  Wifi, 
  Calendar, 
  Gauge, 
  Bell, 
  ChevronRight,
  Zap,
  Shield,
  Droplets
} from 'lucide-react';

const HomePage = () => {
  const { t } = useLanguage();

  const features = [
    { icon: Wifi, title: t('feature_remote'), desc: t('feature_remote_desc') },
    { icon: Calendar, title: t('feature_schedule'), desc: t('feature_schedule_desc') },
    { icon: Gauge, title: t('feature_pressure'), desc: t('feature_pressure_desc') },
    { icon: Bell, title: t('feature_monitor'), desc: t('feature_monitor_desc') },
  ];

  return (
    <div className="min-h-screen bg-fac-dark">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1920&q=80')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-fac-dark/80 via-fac-dark/60 to-fac-dark" />
        </div>

        {/* Noise overlay */}
        <div className="absolute inset-0 noise-overlay pointer-events-none" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center pt-20">
          <div className="animate-slide-up">
            <h1 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl text-white tracking-tighter uppercase mb-4">
              {t('hero_title')}
            </h1>
            <h2 className="font-heading font-bold text-3xl md:text-5xl text-fac-green tracking-tight uppercase mb-6">
              {t('hero_subtitle')}
            </h2>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-8 font-chinese">
              {t('hero_description')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login">
                <Button 
                  size="lg"
                  className="btn-skew bg-fac-green text-black font-bold uppercase tracking-wider hover:bg-fac-green-dim h-14 px-8 text-lg"
                  data-testid="hero-cta-btn"
                >
                  <span>{t('hero_cta')}</span>
                </Button>
              </Link>
              <Link to="/product">
                <Button 
                  size="lg"
                  variant="outline"
                  className="btn-skew border-2 border-white text-white font-bold uppercase tracking-wider hover:bg-white/10 h-14 px-8 text-lg"
                  data-testid="hero-learn-btn"
                >
                  <span>{t('hero_learn_more')}</span>
                </Button>
              </Link>
            </div>
          </div>

          {/* Weather Widget */}
          <div className="mt-16 max-w-md mx-auto">
            <WeatherWidget />
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronRight className="w-8 h-8 text-fac-green rotate-90" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-fac-dark" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-heading font-black text-4xl md:text-5xl text-white tracking-tighter uppercase text-center mb-4">
            {t('features_title')}
          </h2>
          <div className="w-24 h-1 bg-fac-green mx-auto mb-12" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className="bg-fac-card border-border hover:border-fac-green transition-all duration-300 group"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-lg bg-fac-green/10 flex items-center justify-center mb-4 group-hover:bg-fac-green/20 transition-colors">
                    <feature.icon className="w-7 h-7 text-fac-green" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wide mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground font-chinese">
                    {feature.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-fac-paper" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-black text-4xl md:text-5xl text-white tracking-tighter uppercase mb-6">
                WHY FAC VALVE?
                <span className="block text-fac-green font-chinese text-2xl md:text-3xl mt-2">
                  為什麼選擇 FAC 閥門？
                </span>
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-fac-green/10 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-fac-green" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-white uppercase mb-1">
                      Save Time / 節省時間
                    </h4>
                    <p className="text-muted-foreground font-chinese">
                      No need to visit the field. Control everything from your phone.
                      <br />
                      不需親自到田間。用手機即可控制一切。
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-fac-green/10 flex items-center justify-center flex-shrink-0">
                    <Droplets className="w-6 h-6 text-fac-green" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-white uppercase mb-1">
                      Save Water / 節約用水
                    </h4>
                    <p className="text-muted-foreground font-chinese">
                      Precise scheduling reduces water waste.
                      <br />
                      精確排程減少水資源浪費。
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-fac-green/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-fac-green" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-white uppercase mb-1">
                      Reliable / 可靠穩定
                    </h4>
                    <p className="text-muted-foreground font-chinese">
                      Built for farm conditions. Works rain or shine.
                      <br />
                      專為農場環境設計。風雨無阻。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=800&q=80"
                alt="Smart valve in field"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-fac-green rounded-lg -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-fac-dark relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-fac-green/10 to-fac-blue/10" />
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center relative z-10">
          <h2 className="font-heading font-black text-4xl md:text-5xl text-white tracking-tighter uppercase mb-4">
            READY TO START?
          </h2>
          <p className="text-xl text-fac-green font-chinese mb-8">
            準備好開始了嗎？
          </p>
          <Link to="/login">
            <Button 
              size="lg"
              className="btn-skew bg-fac-green text-black font-bold uppercase tracking-wider hover:bg-fac-green-dim h-14 px-12 text-lg"
              data-testid="cta-btn"
            >
              <span>{t('hero_cta')}</span>
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
